import { useTranslate } from '@refinedev/core';
import { Avatar, Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import { AppstoreAddOutlined } from '@ant-design/icons';
import type { OAuthClientOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { OAUTH_CLIENT_SHOW_TABS } from '../constants';
import type { OAuthClientShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<OAuthClientShowTab, string> = {
  general: 'iam.oauth_clients.tabs.general',
  tokens: 'iam.oauth_clients.tabs.tokens',
};

interface OAuthClientShowHeaderProps {
  record: OAuthClientOut;
  activeTab: OAuthClientShowTab;
  onTabChange: (key: string) => void;
}

export const OAuthClientShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: OAuthClientShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <Avatar
          size={64}
          icon={<AppstoreAddOutlined />}
          style={{ backgroundColor: token.colorPrimary }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.client_name || record.client_id}
            </Title>
            {record.is_confidential && (
              <Tag color="purple">
                {t('iam.oauth_clients.fields.is_confidential')}
              </Tag>
            )}
            {record.require_pkce && (
              <Tag color="geekblue">
                {t('iam.oauth_clients.fields.require_pkce')}
              </Tag>
            )}
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id} · `}
            <Text type="secondary" code copyable>
              {record.client_id}
            </Text>
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('iam.oauth_clients.fields.grant_types')}>
              {record.grant_types?.length ?? 0}
            </StatCell>
            <StatCell label={t('iam.oauth_clients.fields.redirect_uris')}>
              {record.redirect_uris?.length ?? 0}
            </StatCell>
            <StatCell label={t('base.fields.created_at')}>
              {orDash(formatDateTime(record.created_at))}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={OAUTH_CLIENT_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
